import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import CwFPagination from 'components/CwFPagination';
import Paper from 'components/Paper';
import LandingList from 'components/LandingList';
import RoundedDisplay from 'components/RoundedDisplay';
import ListWithPopup from 'components/ListWithPopup';
import ActionsMenu from 'components/ActionsMenu';
import { CROP_SEASONS_LANDING_MENU } from 'utilities/menus';
import { formatEpochDate } from 'utilities/dateHelpers';
import { paths } from 'routes/paths';
import cropSeasonTableSort from './helpers/cropSeasonTableSort';
import 'styles/uiKitTableReset.css';
import './custom.css';

const SORT_DIRECTIONS = { ascend: 'asc', descend: 'desc' };

const TABLE_COLUMNS = [
  {
    field: 'name',
    title: 'Season',
    sortable: true
  },
  {
    field: 'active',
    title: 'Season status',
    sortable: true
  },
  {
    field: 'crop',
    title: 'Crop',
    sortable: true
  },
  {
    field: 'totalArea',
    title: 'Total Area',
    sortable: true
  },
  {
    field: 'startDate',
    title: 'Start Date',
    sortable: true
  },
  {
    field: 'endDate',
    title: 'End Date',
    sortable: true
  },
  {
    field: 'createdAt',
    title: 'Created',
    sortable: true
  }
];

const inactiveStyle = cropSeason => ({
  color: cropSeason.active ? '' : '#696F88'
});

const getCropListString = season => {
  return season.crops
    ? season.crops.map((crop, index) =>
        index !== season.crops.length - 1 ? `${crop.name}, ` : `${crop.name}`
      )
    : 'No Crop';
};

const cellRenderers = {
  name: cropSeason => (
    <span className="text-sm font-bold" style={inactiveStyle(cropSeason)}>
      <Link to={`${paths.cropSeasons}/${cropSeason.id}/view`}>
        {cropSeason.name}
      </Link>
    </span>
  ),
  active: cropSeason => (
    <span style={inactiveStyle(cropSeason)}>
      {cropSeason.active ? 'Active' : 'Inactive'}
    </span>
  ),
  crop: cropSeason => (
    <ListWithPopup
      position="right"
      ariaLabel="Crops List"
      delay={1000}
      top="0"
      msg={<div className="w-96">{getCropListString(cropSeason)}</div>}
      component={
        <div className="truncate w-60" style={inactiveStyle(cropSeason)}>
          {getCropListString(cropSeason)}
        </div>
      }
    />
  ),
  totalArea: cropSeason => (
    <span style={inactiveStyle(cropSeason)}>
      <RoundedDisplay>{cropSeason.totalArea?.totalArea || '0'}</RoundedDisplay>
    </span>
  ),
  startDate: cropSeason => (
    <span style={inactiveStyle(cropSeason)}>
      {formatEpochDate(cropSeason.startDateEpoch)}
    </span>
  ),
  endDate: cropSeason => (
    <span style={inactiveStyle(cropSeason)}>
      {formatEpochDate(cropSeason.endDateEpoch)}
    </span>
  ),
  createdAt: (cropSeason, onAction, selectedMember, financialAccess) => (
    <div className="relative flex items-center">
      <div className="mr-8" style={inactiveStyle(cropSeason)}>
        {formatEpochDate(cropSeason.createdDateEpoch)}
      </div>
      <div
        data-testid="crop-season-table-options-menu"
        className="absolute right-0 flex-none w-10"
      >
        <ActionsMenu
          onAction={action => onAction(action, cropSeason)}
          options={CROP_SEASONS_LANDING_MENU}
          userPermissions={{
            role: selectedMember?.role,
            financialAccess
          }}
        />
      </div>
    </div>
  )
};

const cropSeasonTableDefaultPropCropSeasons = [];
const cropSeasonTableDefaultPropHandleAction = () => {};
const cropSeasonTableDefaultPropSelectedMember = {};
const CropSeasonTable = ({
  cropSeasons = cropSeasonTableDefaultPropCropSeasons,
  allCropSeasons,
  tableSort,
  handleAction = cropSeasonTableDefaultPropHandleAction,
  selectedMember = cropSeasonTableDefaultPropSelectedMember,
  financialAccess,
  seasonPageNumber,
  handlePageChange,
  cropSeasonsLength
}) => {
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('');

  const handleSort = (field, dir) => {
    setSortField(field);
    setSortDir(dir);
    tableSort(cropSeasonTableSort(allCropSeasons, field, dir));
  };

  const handleTableChange = (pagination, filters, sorter) => {
    handleSort(sorter?.columnKey, SORT_DIRECTIONS[sorter?.order]);
  };

  const columns = TABLE_COLUMNS.map(({ field, title, sortable }) => ({
    key: field,
    title,
    sorter: sortable,
    sortOrder:
      sortField === field
        ? Object.keys(SORT_DIRECTIONS).find(
            order => SORT_DIRECTIONS[order] === sortDir
          )
        : null,
    render: (_, cropSeason) =>
      cellRenderers[field](
        cropSeason,
        handleAction,
        selectedMember,
        financialAccess
      )
  }));

  return (
    <div
      className="mx-2 my-4 min-w-content"
      data-testid="cropSeasons-table"
      style={{ minWidth: 'min-content' }}
    >
      <Paper className="cwf-table-flush cwf-table-no-row-hover">
        {CwFPagination(
          cropSeasonsLength || 0,
          seasonPageNumber,
          handlePageChange
        )}
        {cropSeasons.length ? (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={cropSeasons}
            pagination={false}
            onChange={handleTableChange}
            rowClassName={cropSeason =>
              cropSeason.active ? '' : 'crop-season-row-inactive'
            }
          />
        ) : (
          // The caller only guards on the unfiltered season count, so a search
          // that matches nothing lands here rather than on its empty state.
          <LandingList.LandingEmpty
            listType="Crop Seasons"
            routePath={`${paths.cropSeasons}/create`}
          />
        )}
        <div className="pb-12">
          {CwFPagination(
            cropSeasonsLength || 0,
            seasonPageNumber,
            handlePageChange
          )}
        </div>
      </Paper>
    </div>
  );
};

CropSeasonTable.propTypes = {
  cropSeasons: PropTypes.arrayOf(PropTypes.object),
  allCropSeasons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleAction: PropTypes.func,
  selectedMember: PropTypes.instanceOf(Object),
  financialAccess: PropTypes.string.isRequired,
  seasonPageNumber: PropTypes.number.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  cropSeasonsLength: PropTypes.number.isRequired,
  tableSort: PropTypes.func.isRequired
};

export default CropSeasonTable;
